import type { PagingViewModel } from 'autogen/swagger/all';
import useGA4Tracking from 'modules/tracking/GA4';
import { useSearchParams } from 'react-router-dom';

export type PaginationCallback = (page: number) => void;

export default function usePagination(
    pagingModel: PagingViewModel,
    queryStringName = pagingModel.queryStringName,
    onPaginateCallback?: PaginationCallback
) {
    const [searchParams, setSearchParams] = useSearchParams();
    const GA4Tracking = useGA4Tracking();

    /* Server State (from Model) */
    const hasNextPage = !!pagingModel.nextPage;
    const hasPreviousPage = !!pagingModel.prevPage;
    const initialCurrentPage = pagingModel.currentPage;

    /* Client state (from SearchParams or initialCurrentPage to SSR) */
    const currentPageNumber = Number(searchParams.get(queryStringName)) || initialCurrentPage;

    const changePage = (pageNumber: number) => {
        if (currentPageNumber === pageNumber) return;

        setSearchParams((prevSearchParams) => {
            const pageNumberToString = pageNumber.toString();

            prevSearchParams.delete(queryStringName);
            prevSearchParams.append(queryStringName, pageNumberToString);

            return prevSearchParams;
        });

        GA4Tracking({
            eventName: 'change_page',
            category: 'navigation',
            eventParams: [
                { param_name: 'page_number', param_value: pageNumber },
                { param_name: 'list_name', param_value: currentPageNumber }
            ]
        });

        if (onPaginateCallback) onPaginateCallback(pageNumber);
    };

    const nextPage = () => {
        if (hasNextPage) {
            changePage(currentPageNumber + 1);
        }
    };

    const previousPage = () => {
        if (hasPreviousPage) {
            changePage(currentPageNumber - 1);
        }
    };

    const resetPagination = () => {
        setSearchParams((prevSearchParams) => {
            prevSearchParams.delete(queryStringName);
            return prevSearchParams;
        });
    };

    return {
        nextPage,
        previousPage,
        changePage,
        resetPagination,
        hasNextPage,
        hasPreviousPage,
        currentPageNumber
    };
}

export const resetPagination = (searchParams: URLSearchParams, queryStringName = 'page') => {
    searchParams.delete(queryStringName);
    return searchParams;
};

export function useResetPagination(queryStringName = 'page') {
    const [searchParams] = useSearchParams();

    return () => resetPagination(searchParams, queryStringName);
}
